'use client';

import type { User } from '@auth0/auth0-react';
import { Auth0Provider } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useCallback } from 'react';

import { clearLastActiveCookie } from '@libs/core';

import { useDecisions } from '../..';
import {
    clearAuth0FlowNavigation,
    confirmAuth0AutoshipEnrollCallback,
    getAuth0RedirectOrigin,
    persistAuth0ReturnTo,
    readAuth0ReturnTo,
    sanitizeSegmentUrl,
} from '../../utils';
import type { Auth0AppState } from '../../utils/getAuth0LoginRedirectOptions';
import { Auth0TokenSync } from './Auth0TokenSync';
import { getAuth0SpaClientOptions } from './auth0ClientOptions';
import { clearAuth0CallbackSearch, setPendingAuth0ReturnTo } from './pendingAuth0ReturnTo';
import { resolveAuth0Destination } from './resolveAuth0Destination';

type Auth0AppProviderProps = {
    readonly children: ReactNode;
};

/**
 * Wraps the app in `Auth0Provider` when `auth0` is on.
 * Otherwise renders children unchanged so the legacy auth flow is preserved.
 *
 * Uses a single fixed callback URL (`/auth/callback`) registered in the Auth0
 * dashboard. The originating page is stored in `appState.returnTo` so the
 * callback handler can redirect the user back after login.
 *
 * `onRedirectCallback` persists `returnTo` for cart merge (keeping a value
 * already stored at login, unless this is a new registration whose destination
 * was resolved to About You), stashes it for the callback redirect, and strips
 * OAuth `code`/`state` from the URL. `Auth0TokenSync` writes the `accessToken`
 * cookie, waits for guest-cart merge on the callback route, then
 * document-navigates so cookie-gated routes (Treats, account) do not race
 * the first landing.
 *
 * Mounted from the shared `PageLayout` so all routes that go through it
 * (ACP, sign-in, cart, etc.) get a single Auth0 session context.
 */
export function Auth0AppProvider({ children }: Auth0AppProviderProps) {
    const isAuth0Enabled = useDecisions('auth0');
    const redirectOrigin = getAuth0RedirectOrigin();

    const onRedirectCallback = useCallback(
        (appState?: Auth0AppState, user?: User) => {
            // Drop lastActive and auth0Reauth left by inactivity/logout so post-login sync is allowed.
            clearLastActiveCookie();
            clearAuth0FlowNavigation();
            confirmAuth0AutoshipEnrollCallback(appState?.isInStoreAutoship === true);
            const returnTo = sanitizeSegmentUrl(resolveAuth0Destination(appState, user), redirectOrigin, {
                includeSearch: true,
            });
            const appStateReturnTo = sanitizeSegmentUrl(appState?.returnTo ?? '/', redirectOrigin, {
                includeSearch: true,
            });
            const loginReturnTo = readAuth0ReturnTo();
            // Registration can upgrade Home/Treats (or an implicit header CTA) to About You after login.
            // Cart merge reads session storage after the callback, so keep the login-time path only
            // when resolution still matches what was stored in appState at authorize time.
            const mergeReturnTo = returnTo === appStateReturnTo ? loginReturnTo || returnTo : returnTo;
            persistAuth0ReturnTo(mergeReturnTo);
            setPendingAuth0ReturnTo(returnTo);
            clearAuth0CallbackSearch();
        },
        [redirectOrigin]
    );

    if (!isAuth0Enabled) {
        return children;
    }

    // Iframe fallback stays off on this client. A revoked refresh token must surface
    // as invalid_grant so handleAccessTokenSyncError can log the user out. Cold-start
    // SSO in useSilentSsoProbe enables fallback only after missing_refresh_token.
    return (
        <Auth0Provider {...getAuth0SpaClientOptions()} onRedirectCallback={onRedirectCallback}>
            <Auth0TokenSync />
            {children}
        </Auth0Provider>
    );
}
